import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { fmtMoney } from '../utils/formatters';
interface HiringFunnelProps {
  estimatedHires: number;
}
function HiringFunnel({
  estimatedHires
}: HiringFunnelProps) {
  // Fixed conversion rates (non-editable)
  const rates = {
    offeredToHired: 85,
    interviewedToOffered: 55,
    scheduledToInterviewed: 65,
    qualifiedToScheduled: 70,
    screenedToQualified: 60,
    approvedToScreened: 50,
    applicationsToApproved: 50
  };
  // Calculate backwards from estimatedHires to get required applications
  const applications = Math.round(estimatedHires / (rates.offeredToHired / 100) / (rates.interviewedToOffered / 100) / (rates.scheduledToInterviewed / 100) / (rates.qualifiedToScheduled / 100) / (rates.screenedToQualified / 100) / (rates.approvedToScreened / 100) / (rates.applicationsToApproved / 100));
  // Calculate forward through the funnel
  const approved = Math.round(applications * (rates.applicationsToApproved / 100));
  const screened = Math.round(approved * (rates.approvedToScreened / 100));
  const qualified = Math.round(screened * (rates.screenedToQualified / 100));
  const scheduled = Math.round(qualified * (rates.qualifiedToScheduled / 100));
  const interviewed = Math.round(scheduled * (rates.scheduledToInterviewed / 100));
  const offered = Math.round(interviewed * (rates.interviewedToOffered / 100));
  const hired = estimatedHires;
  const stages = [{
    label: 'Applications',
    value: applications,
    gradient: 'from-gray-300 to-gray-200',
    rate: rates.applicationsToApproved
  }, {
    label: 'Approved',
    value: approved,
    gradient: 'from-gray-400 to-gray-300',
    rate: rates.approvedToScreened
  }, {
    label: 'Screened',
    value: screened,
    gradient: 'from-blue-500 to-blue-400',
    rate: rates.screenedToQualified
  }, {
    label: 'Qualified',
    value: qualified,
    gradient: 'from-indigo-400 to-indigo-300',
    rate: rates.qualifiedToScheduled
  }, {
    label: 'Scheduled',
    value: scheduled,
    gradient: 'from-indigo-500 to-indigo-400',
    rate: rates.scheduledToInterviewed
  }, {
    label: 'Interviewed',
    value: interviewed,
    gradient: 'from-purple-400 to-purple-300',
    rate: rates.interviewedToOffered
  }, {
    label: 'Offered',
    value: offered,
    gradient: 'from-amber-500 to-amber-400',
    rate: rates.offeredToHired
  }, {
    label: 'Hired',
    value: hired,
    gradient: 'from-navy to-navy/90',
    rate: null
  }];
  const maxValue = stages[0].value;
  return <div className="space-y-1" data-id="element-1008">
      {stages.map((stage, index) => <div key={index} data-id="element-1009">
          <div className="flex items-center gap-2 md:gap-4" data-id="element-1010">
            <div className="w-6 md:w-8 text-right flex-shrink-0" data-id="element-1011">
              <span className="text-base md:text-xl font-bold text-navy" data-id="element-1012">
                {stage.value}
              </span>
            </div>
            <div className="w-24 md:w-48 flex-shrink-0" data-id="element-1013">
              <span className="text-xs md:text-base font-semibold text-gray-700 whitespace-nowrap" data-id="element-1014">
                {stage.label}
              </span>
            </div>
            {stage.gradient && <div className="flex-1 h-6 md:h-8 bg-gray-200 rounded-full overflow-hidden" data-id="element-1015">
                <div className={`h-full bg-gradient-to-r ${stage.gradient} transition-all duration-500 ease-out`} style={{
            width: `${stage.value / maxValue * 100}%`
          }} data-id="element-1016" />
              </div>}
            <div className="w-14 md:w-20 flex-shrink-0" data-id="element-1017" />
          </div>

          {/* Conversion rate positioned between bars */}
          {stage.rate !== null && <div className="flex items-center gap-2 md:gap-4 py-1" data-id="element-1018">
              <div className="w-6 md:w-8 flex-shrink-0" data-id="element-1019" />
              <div className="w-24 md:w-48 flex-shrink-0" data-id="element-1020" />
              <div className="flex-1 flex items-center justify-center" data-id="element-1021">
                <span className="text-xs md:text-sm font-semibold text-gray-600" data-id="element-1022">
                  ↓ {stage.rate}%
                </span>
              </div>
              <div className="w-14 md:w-20 flex-shrink-0" data-id="element-1023" />
            </div>}
        </div>)}
    </div>;
}
interface RetentionFunnelProps {
  estimatedHires: number;
}
function RetentionFunnel({
  estimatedHires
}: RetentionFunnelProps) {
  // Retention rates (editable with sliders)
  const [rates, setRates] = useState({
    hiredToLaunched: 70,
    launchedTo30d: 30,
    day30To90d: 80,
    day90To1Year: 30
  });
  // Calculate retention values based on rates
  const launched = Math.round(estimatedHires * (rates.hiredToLaunched / 100));
  const active30 = Math.round(launched * (rates.launchedTo30d / 100));
  const active90Plus = Math.round(active30 * (rates.day30To90d / 100));
  const active1Year = Math.round(active90Plus * (rates.day90To1Year / 100));
  const stages = [{
    label: 'Hired',
    value: estimatedHires,
    gradient: 'from-navy to-navy/90',
    rate: null,
    rateKey: null
  }, {
    label: 'Launched',
    value: launched,
    gradient: 'from-amber-500 to-amber-400',
    rate: rates.hiredToLaunched,
    rateKey: 'hiredToLaunched'
  }, {
    label: 'Active 30d',
    value: active30,
    gradient: 'from-blue-500 to-blue-400',
    rate: rates.launchedTo30d,
    rateKey: 'launchedTo30d'
  }, {
    label: 'Active 90d+',
    value: active90Plus,
    gradient: 'from-green-500 to-green-400',
    rate: rates.day30To90d,
    rateKey: 'day30To90d'
  }, {
    label: 'Active 1 Year',
    value: active1Year,
    gradient: 'from-purple-500 to-purple-400',
    rate: rates.day90To1Year,
    rateKey: 'day90To1Year'
  }];
  const maxValue = stages[0].value;
  const handleRateChange = (rateKey: string, value: number) => {
    setRates({
      ...rates,
      [rateKey]: value
    });
  };
  return <div className="space-y-1" data-id="element-1024">
      {stages.map((stage, index) => <div key={index} data-id="element-1025">
          {/* Slider above the bar */}
          {stage.rate !== null && stage.rateKey && <div className="mb-2" data-id="element-1026">
              <div className="ml-[calc(1.5rem+6rem)] md:ml-[calc(2rem+12rem)] mr-[3.5rem] md:mr-[5rem]" data-id="element-1027">
                <div className="flex items-center justify-between mb-1" data-id="element-1028">
                  <span className="text-xs text-gray-500" data-id="element-1029">Retention Rate</span>
                  <span className="text-sm font-bold text-amber" data-id="element-1030">
                    {stage.rate}%
                  </span>
                </div>
                <input type="range" min="5" max="100" step="5" value={stage.rate} onChange={e => handleRateChange(stage.rateKey!, parseFloat(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-amber" style={{
            background: `linear-gradient(to right, #F59E0B 0%, #F59E0B ${(stage.rate - 5) / (100 - 5) * 100}%, #E5E7EB ${(stage.rate - 5) / (100 - 5) * 100}%, #E5E7EB 100%)`
          }} data-id="element-1031" />
                <div className="flex justify-between text-xs text-gray-500 mt-1" data-id="element-1032">
                  <span data-id="element-1033">5%</span>
                  <span data-id="element-1034">100%</span>
                </div>
              </div>
            </div>}

          {/* Bar */}
          <div className="flex items-center gap-2 md:gap-4" data-id="element-1035">
            <div className="w-6 md:w-8 text-right flex-shrink-0" data-id="element-1036">
              <span className="text-base md:text-xl font-bold text-navy" data-id="element-1037">
                {stage.value}
              </span>
            </div>
            <div className="w-24 md:w-48 flex-shrink-0" data-id="element-1038">
              <span className="text-xs md:text-base font-semibold text-gray-700 whitespace-nowrap" data-id="element-1039">
                {stage.label}
              </span>
            </div>
            <div className="flex-1 h-6 md:h-8 bg-gray-200 rounded-full overflow-hidden" data-id="element-1040">
              <div className={`h-full bg-gradient-to-r ${stage.gradient} transition-all duration-500 ease-out`} style={{
            width: `${stage.value / maxValue * 100}%`
          }} data-id="element-1041" />
            </div>
            <div className="w-14 md:w-20 flex-shrink-0" data-id="element-1042" />
          </div>

          {/* Conversion rate positioned between bars */}
          {stage.rate !== null && <div className="flex items-center gap-2 md:gap-4 py-2" data-id="element-1043">
              <div className="w-6 md:w-8 flex-shrink-0" data-id="element-1044" />
              <div className="w-24 md:w-48 flex-shrink-0" data-id="element-1045" />
              <div className="flex-1 flex items-center justify-center" data-id="element-1046">
                <span className="text-xs md:text-sm font-semibold text-amber" data-id="element-1047">
                  ↓ {stage.rate}%
                </span>
              </div>
              <div className="w-14 md:w-20 flex-shrink-0" data-id="element-1048" />
            </div>}
        </div>)}
    </div>;
}
export function RetentionBreakdown() {
  const navigate = useNavigate();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const [adminHourlyRate, setAdminHourlyRate] = useState(40);
  const [leadershipHourlyRate, setLeadershipHourlyRate] = useState(100);
  const [adSpend, setAdSpend] = useState(4000);
  const [cph, setCph] = useState(500);
  const efficiencyOptions = [{
    label: 'High Efficiency',
    value: 250
  }, {
    label: 'Average Efficiency',
    value: 500
  }, {
    label: 'Low Efficiency',
    value: 750
  }];
  const storedInputs = localStorage.getItem('calculatorInputs');
  const inputs = storedInputs ? JSON.parse(storedInputs) : {};
  const estimatedHires = Math.round((adSpend || 4000) / (cph || 500));
  const estimatedLaunched = Math.round(estimatedHires * 0.7);
  const preInterviewHours = estimatedHires * 5;
  const onboardingTrainingHours = estimatedLaunched * 5;
  const preInterviewCost = preInterviewHours * adminHourlyRate;
  const onboardingTrainingCost = onboardingTrainingHours * leadershipHourlyRate;
  const handleContinue = () => {
    const updatedInputs = {
      ...inputs,
      adSpend,
      cph
    };
    localStorage.setItem('calculatorInputs', JSON.stringify(updatedInputs));
    navigate('/contact');
  };
  return <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white" data-id="element-1049">
      {/* Header */}
      <header className="border-b-2 border-gray-200 bg-white" data-id="element-1050">
        <div className="max-w-[1400px] mx-auto px-4 py-3 md:py-6 flex items-center justify-between" data-id="element-1051">
          <img src="https://cdn.magicpatterns.com/uploads/jrZpZA6KS7cefrnJNRcqNs/Copy_of_HIRE.png" alt="HIRE" className="h-8 md:h-12 cursor-pointer" onClick={() => navigate('/')} data-id="element-1052" />
          <div className="px-3 py-1 bg-amber/10 border border-amber/30 rounded-full text-amber-700 text-xs md:text-sm font-semibold" data-id="element-1053">
            YEAR-ROUND PROGRAM
          </div>
        </div>
      </header>

      <div className="max-w-[1000px] mx-auto px-4 py-4 md:py-6" data-id="element-1054">
        {/* Hero */}
        <div className="text-center mb-8 md:mb-12" data-id="element-1055">
          <h1 className="text-3xl md:text-5xl font-bold text-navy mb-3 md:mb-4" data-id="element-1056">
            Understanding Your Recruiting Funnel
          </h1>
          <p className="text-base md:text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-1057">
            Before we show your results, let's break down the metrics that drive
            your ROI
          </p>
        </div>

        {/* Recruiting Assumptions Section */}
        <div className="bg-white border-2 border-gray-200 rounded-lg shadow-lg p-6 md:p-8 mb-8" data-id="element-1058">
          <div className="border-b-2 border-gray-200 pb-3 mb-6" data-id="element-1059">
            <h2 className="text-lg font-bold text-navy" data-id="element-1060">
              Recruiting Assumptions
            </h2>
            <p className="text-sm text-gray-600 mt-1" data-id="element-1061">
              Adjust these values to see how they impact your funnel
            </p>
          </div>

          <div className="space-y-6" data-id="element-1062">
            {/* Monthly Ad Spend */}
            <div data-id="element-1063">
              <div className="flex items-center justify-between mb-3" data-id="element-1064">
                <label className="text-sm font-semibold text-gray-700" data-id="element-1065">
                  Monthly Ad Spend (Indeed/Job Boards)
                </label>
                <span className="text-lg font-bold text-navy" data-id="element-1066">
                  {fmtMoney(adSpend)}
                </span>
              </div>
              <input type="range" min="500" max="10000" step="500" value={adSpend} onChange={e => setAdSpend(parseFloat(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-amber" style={{
              background: `linear-gradient(to right, #F59E0B 0%, #F59E0B ${(adSpend - 500) / (10000 - 500) * 100}%, #E5E7EB ${(adSpend - 500) / (10000 - 500) * 100}%, #E5E7EB 100%)`
            }} data-id="element-1067" />
              <div className="flex justify-between text-xs text-gray-500 mt-1" data-id="element-1068">
                <span data-id="element-1069">$500</span>
                <span data-id="element-1070">$10,000</span>
              </div>
            </div>

            {/* Cost Per Hire - Button Options */}
            <div data-id="element-1071">
              <label className="block text-sm font-semibold text-gray-700 mb-3" data-id="element-1072">
                Ad Spend Cost Per Hire
              </label>
              <div className="grid grid-cols-3 gap-3" data-id="element-1073">
                {efficiencyOptions.map(option => <button key={option.value} onClick={() => setCph(option.value)} className={`px-3 py-4 rounded-lg border-2 font-semibold transition-all ${cph === option.value ? 'bg-amber border-amber text-navy shadow-lg' : 'bg-white border-gray-200 text-gray-700 hover:border-amber hover:bg-amber/10'}`} data-id="element-1074">
                    <div className="text-base md:text-lg font-bold mb-1" data-id="element-1075">
                      {option.label.split(' ')[0]}
                    </div>
                    <div className="text-xs text-gray-600 mb-2" data-id="element-1076">
                      {option.label.split(' ')[1]}
                    </div>
                    <div className="text-sm md:text-base font-semibold" data-id="element-1077">
                      {fmtMoney(option.value)}
                    </div>
                  </button>)}
              </div>
            </div>

            {/* Estimated Hires Display */}
            <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4" data-id="element-1078">
              <div className="flex items-center justify-between" data-id="element-1079">
                <label className="text-sm font-semibold text-gray-700" data-id="element-1080">
                  Estimated Monthly Hires
                </label>
                <span className="text-2xl font-bold text-navy" data-id="element-1081">
                  {estimatedHires}
                </span>
              </div>
              <p className="text-xs text-gray-600 mt-2" data-id="element-1082">
                Based on {fmtMoney(adSpend)} ÷ {fmtMoney(cph)} per hire
              </p>
            </div>
          </div>
        </div>

        {/* Main Container */}
        <div className="bg-white border-2 border-gray-200 rounded-lg shadow-lg p-6 md:p-8 space-y-8" data-id="element-1083">
          {/* Overhead Section */}
          <div className="space-y-4" data-id="element-1084">
            <div className="border-b-2 border-gray-200 pb-3" data-id="element-1085">
              <h2 className="text-lg font-bold text-navy" data-id="element-1086">
                Overhead Assumptions
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1087">
              {/* Time Investment */}
              <div className="bg-orange-50 border-2 border-orange-200 rounded-lg p-6" data-id="element-1088">
                <h3 className="text-base font-semibold text-gray-700 mb-4" data-id="element-1089">
                  Time Investment
                </h3>
                <div className="space-y-4" data-id="element-1090">
                  <div data-id="element-1091">
                    <label className="block text-xs font-semibold uppercase tracking-wide text-gray-600 mb-2" data-id="element-1092">
                      Processing, Screening, and Interviewing
                    </label>
                    <div className="text-xl font-bold text-navy" data-id="element-1093">
                      {preInterviewHours} hours
                    </div>
                    <p className="text-xs text-gray-600 mt-1" data-id="element-1094">
                      5 hours × {estimatedHires} hires
                    </p>
                  </div>
                  <div data-id="element-1095">
                    <label className="block text-xs font-semibold uppercase tracking-wide text-gray-600 mb-2" data-id="element-1096">
                      Hiring, Onboarding, and Training
                    </label>
                    <div className="text-xl font-bold text-navy" data-id="element-1097">
                      {onboardingTrainingHours} hours
                    </div>
                    <p className="text-xs text-gray-600 mt-1" data-id="element-1098">
                      5 hours × {estimatedLaunched} launched reps
                    </p>
                  </div>
                </div>
              </div>

              {/* Hourly Rates & Costs */}
              <div className="bg-red-50 border-2 border-red-200 rounded-lg p-6" data-id="element-1099">
                <h3 className="text-base font-semibold text-gray-700 mb-4" data-id="element-1100">
                  Hourly Rates & Costs
                </h3>
                <div className="space-y-4" data-id="element-1101">
                  <div data-id="element-1102">
                    <label className="block text-xs font-semibold uppercase tracking-wide text-gray-600 mb-2" data-id="element-1103">
                      Admin Hourly Rate
                    </label>
                    <div className="flex items-center justify-between mb-2" data-id="element-1104">
                      <span className="text-lg font-bold text-navy" data-id="element-1105">
                        {fmtMoney(adminHourlyRate)}/hr
                      </span>
                    </div>
                    <input type="range" min="10" max="75" step="5" value={adminHourlyRate} onChange={e => setAdminHourlyRate(parseFloat(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-amber" style={{
                    background: `linear-gradient(to right, #F59E0B 0%, #F59E0B ${(adminHourlyRate - 10) / (75 - 10) * 100}%, #E5E7EB ${(adminHourlyRate - 10) / (75 - 10) * 100}%, #E5E7EB 100%)`
                  }} data-id="element-1106" />
                    <div className="flex justify-between text-xs text-gray-500 mt-1 mb-2" data-id="element-1107">
                      <span data-id="element-1108">$10</span>
                      <span data-id="element-1109">$75</span>
                    </div>
                    <div className="text-sm font-semibold text-gray-700" data-id="element-1110">
                      Cost: {fmtMoney(preInterviewCost)}
                    </div>
                  </div>
                  <div data-id="element-1111">
                    <label className="block text-xs font-semibold uppercase tracking-wide text-gray-600 mb-2" data-id="element-1112">
                      Leadership Hourly Rate
                    </label>
                    <div className="flex items-center justify-between mb-2" data-id="element-1113">
                      <span className="text-lg font-bold text-navy" data-id="element-1114">
                        {fmtMoney(leadershipHourlyRate)}/hr
                      </span>
                    </div>
                    <input type="range" min="40" max="200" step="5" value={leadershipHourlyRate} onChange={e => setLeadershipHourlyRate(parseFloat(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-amber" style={{
                    background: `linear-gradient(to right, #F59E0B 0%, #F59E0B ${(leadershipHourlyRate - 40) / (200 - 40) * 100}%, #E5E7EB ${(leadershipHourlyRate - 40) / (200 - 40) * 100}%, #E5E7EB 100%)`
                  }} data-id="element-1115" />
                    <div className="flex justify-between text-xs text-gray-500 mt-1 mb-2" data-id="element-1116">
                      <span data-id="element-1117">$40</span>
                      <span data-id="element-1118">$200</span>
                    </div>
                    <div className="text-sm font-semibold text-gray-700" data-id="element-1119">
                      Cost: {fmtMoney(onboardingTrainingCost)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Recruiting Metrics Section */}
          <div className="space-y-4" data-id="element-1120">
            <div className="border-b-2 border-gray-200 pb-3" data-id="element-1121">
              <h2 className="text-lg font-bold text-navy" data-id="element-1122">
                Recruiting Metrics
              </h2>
              <p className="text-sm text-gray-600 mt-1" data-id="element-1123">
                Standard industry conversion rates
              </p>
            </div>
            <HiringFunnel estimatedHires={estimatedHires} data-id="element-1124" />
          </div>

          {/* Retention Metrics Section */}
          <div className="space-y-4" data-id="element-1125">
            <div className="border-b-2 border-gray-200 pb-3" data-id="element-1126">
              <h2 className="text-lg font-bold text-navy" data-id="element-1127">Retention Metrics</h2>
              <p className="text-sm text-gray-600 mt-1" data-id="element-1128">
                Adjust the sliders to match your retention expectations
              </p>
            </div>
            <RetentionFunnel estimatedHires={estimatedHires} data-id="element-1129" />
          </div>
        </div>

        {/* CTA */}
        <div className="mt-8 text-center" data-id="element-1130">
          <button onClick={handleContinue} className="inline-flex items-center gap-3 px-8 py-4 bg-amber hover:bg-amber-dark text-navy font-bold text-lg rounded-lg transition-all shadow-xl hover:shadow-amber/50 hover:scale-105 active:scale-100" data-id="element-1131">
            <span data-id="element-1132">Continue to Full Results</span>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-1133">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M13 7l5 5m0 0l-5 5m5-5H6" data-id="element-1134" />
            </svg>
          </button>
        </div>
      </div>
    </div>;
}